/**
 * PTEL Visor - Infrastructure Popup Component
 * V04 - Popup informativo para infraestructuras normalizadas
 * 
 * Muestra información detallada de una infraestructura:
 * - Icono y nombre
 * - Dirección y municipio
 * - Coordenadas UTM formateadas
 * - Barra visual de score con colores
 * - Fuente de geocodificación
 * 
 * @see PTEL Design System §7 para colores de score
 */

import React, { memo, useMemo } from 'react';
import { Popup, Tooltip } from 'react-leaflet';
import type { NormalizedInfrastructure, InfrastructureType } from '../../types/infrastructure';
import { VIA_LABELS_LARGO } from '../../utils/procedenciaVia';
import { getMarkerConfig } from '../../constants/markerConfig';
import { formatUTM } from '../../lib/leaflet-crs';
import './infrastructure.css';

// =============================================================================
// TIPOS
// =============================================================================

export interface InfrastructurePopupProps {
  /** Infraestructura a mostrar */
  infrastructure: NormalizedInfrastructure;
  /** Mostrar coordenadas UTM (default: true) */
  showUTM?: boolean;
  /** Mostrar score de validación (default: true) */
  showScore?: boolean;
  /** Mostrar fuente de geocodificación (default: true) */
  showSource?: boolean;
  /** Mostrar dirección (default: true) */
  showAddress?: boolean;
}

export interface InfrastructureTooltipProps {
  /** Nombre a mostrar */
  name: string;
  /** Tipo de infraestructura */
  type: InfrastructureType;
}

// =============================================================================
// CONSTANTES
// =============================================================================

/** Umbrales de score para colores */
const SCORE_THRESHOLDS = {
  EXCELLENT: 90,
  GOOD: 70,
  FAIR: 50,
} as const;

// =============================================================================
// UTILIDADES
// =============================================================================

/**
 * Obtiene la clase CSS según el score
 */
function getScoreClass(score: number): string {
  if (score >= SCORE_THRESHOLDS.EXCELLENT) return 'ptel-score-excellent';
  if (score >= SCORE_THRESHOLDS.GOOD) return 'ptel-score-good';
  if (score >= SCORE_THRESHOLDS.FAIR) return 'ptel-score-fair';
  return 'ptel-score-poor';
}

/**
 * Formatea la ubicación (municipio + provincia)
 */
function formatLocation(municipality?: string, province?: string): string | null {
  if (!municipality && !province) return null;
  if (municipality && province) return `${municipality} (${province})`;
  return municipality ?? province ?? null;
}

// =============================================================================
// ICONOS SVG
// =============================================================================

const LocationIcon = () => (
  <svg className="ptel-popup-row-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const DatabaseIcon = () => (
  <svg className="ptel-popup-source-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <ellipse cx="12" cy="5" rx="9" ry="3" />
    <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
    <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
  </svg>
);

// =============================================================================
// COMPONENTE POPUP
// =============================================================================

/**
 * Popup informativo para infraestructuras PTEL
 * 
 * Diseñado según el PTEL Design System con:
 * - Tipografía Space Grotesk / JetBrains Mono
 * - Colores de la paleta Normaliza/Descarga
 * - Barra de score con 4 niveles de color
 * 
 * @example
 * ```tsx
 * <Marker position={[lat, lng]}>
 *   <InfrastructurePopup infrastructure={infra} />
 * </Marker>
 * ```
 */
export const InfrastructurePopup = memo<InfrastructurePopupProps>(function InfrastructurePopup({
  infrastructure,
  showUTM = true,
  showScore = true,
  showSource = true,
  showAddress = true,
}) {
  const { name, type, x, y, score, address, municipality, province, source, procedencia, aviso } = infrastructure;

  // MAPCEN · DOP-4=(b): el bloque «Fuente» habla en vías (`VIA_LABELS_LARGO`),
  // no en sufijos del pipeline; `source` crudo solo como respaldo para
  // consumidores legados que no rellenan `procedencia`. Con centroide, el
  // aviso del entregable (`avisoCentroide`) debajo.
  const fuenteTexto = procedencia ? VIA_LABELS_LARGO[procedencia] : source;
  
  // Obtener configuración del marcador (icon, color)
  const markerConfig = useMemo(() => getMarkerConfig(type), [type]);
  
  // Clase CSS para el score
  const scoreClass = useMemo(() => getScoreClass(score), [score]);
  
  // Formatear ubicación
  const location = useMemo(
    () => formatLocation(municipality, province),
    [municipality, province]
  );
  
  // Formatear coordenadas UTM
  const utmFormatted = useMemo(
    () => formatUTM(x, y, 2),
    [x, y]
  );

  return (
    <Popup className="ptel-infrastructure-popup" closeButton={true} autoPan={true}>
      {/* Header con icono y nombre */}
      <div className="ptel-popup-header">
        <div 
          className="ptel-popup-icon"
          style={{ backgroundColor: markerConfig.color }}
          aria-hidden="true"
        >
          <span style={{ fontSize: '16px' }}>{markerConfig.icon}</span>
        </div>
        <span className="ptel-popup-name">{name}</span>
      </div>
      
      {/* Cuerpo con información */}
      <div className="ptel-popup-body">
        {/* Dirección */}
        {showAddress && (address || location) && (
          <div className="ptel-popup-row">
            <LocationIcon />
            <div className="ptel-popup-row-content">
              {address && <div className="ptel-popup-address">{address}</div>}
              {location && <div>{location}</div>}
            </div>
          </div>
        )}
        
        {/* Coordenadas UTM */}
        {showUTM && (
          <div className="ptel-popup-coords">
            <div className="ptel-popup-coords-label">UTM30 ETRS89</div>
            {utmFormatted}
          </div>
        )}
        
        {/* Score de validación */}
        {showScore && (
          <div className={`ptel-popup-score ${scoreClass}`}>
            <div className="ptel-popup-score-header">
              <span className="ptel-popup-score-label">Score</span>
              <span className="ptel-popup-score-value">{score}/100</span>
            </div>
            <div className="ptel-popup-score-bar">
              <div 
                className="ptel-popup-score-fill"
                style={{ width: `${score}%` }}
              />
            </div>
          </div>
        )}
        
        {/* Fuente de geocodificación (vía, no source crudo — MAPCEN) */}
        {showSource && fuenteTexto && (
          <div className="ptel-popup-source" data-testid="popup-fuente">
            <DatabaseIcon />
            <span>{fuenteTexto}</span>
          </div>
        )}
        {showSource && procedencia === 'centroide' && aviso && (
          <div className="ptel-popup-aviso-centroide" role="note" data-testid="popup-aviso">
            {aviso}
          </div>
        )}
      </div>
    </Popup>
  );
});

// =============================================================================
// COMPONENTE TOOLTIP
// =============================================================================

/**
 * Tooltip rápido para hover sobre marcadores
 * 
 * Muestra solo el nombre de la infraestructura.
 */
export const InfrastructureTooltip = memo<InfrastructureTooltipProps>(function InfrastructureTooltip({
  name,
  type,
}) {
  const markerConfig = useMemo(() => getMarkerConfig(type), [type]);
  
  return (
    <Tooltip 
      className="ptel-infrastructure-tooltip"
      direction="top"
      offset={[0, -10]}
      opacity={1}
    >
      <span>{markerConfig.icon} {name}</span>
    </Tooltip>
  );
});

// =============================================================================
// EXPORTS
// =============================================================================

export default InfrastructurePopup;
